import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ChevronDown, ChevronRight, Repeat } from "lucide-react";
import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Mission } from "@/campaigns/modules/missions/types";
import {
  isRepeatable,
  missionRewardExtraCount,
  missionRewardGames,
  missionRewardLabel,
  parseProgressValue,
} from "@/campaigns/modules/missions/types";
import { MissionTaskRow } from "./mission-task-row";
import { taskVariant, taskVariantInputs } from "./task-variant";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PrizeText } from "@/components/prize-text";
import {
  StepActiveIcon,
  StepDoneIcon,
  StepLockedIcon,
} from "@/components/icons/step-state-icons";

/** The mission's rung state in the ladder — derived by `MissionLadder`. */
export type MissionCardState = "completed" | "active" | "locked";

/** The state's name for screen readers. */
const stepStateLabel = (state: MissionCardState, t: TFunction): string =>
  state === "completed"
    ? t("missions.completedPill", "Completed")
    : state === "active"
      ? t("missions.inProgress", "In progress")
      : t("missions.locked", "Locked");

/** "Step 2 of 6" — the order, which the UI never calls a mission (partner branding). */
const stepOfLabel = (position: number, total: number, t: TFunction): string =>
  t("missions.stepOf", "Step {{current}} of {{total}}", {
    current: position,
    total,
  });

/**
 * The rung's position marker: a numbered circle straddling the card's TOP
 * border, horizontally centered — the same anchor for every state (completed,
 * active, locked), so the ladder reads as one stepper rail. The NUMBER carries
 * the order — partners name this feature differently (quests, challenges, …),
 * so the UI never says "mission". Opaque card-surface fill so the card border
 * doesn't run through it. Hidden entirely on single-mission campaigns.
 */
const EdgePositionBadge = ({
  position,
  total,
  state,
}: {
  position: number;
  total: number;
  state: MissionCardState;
}) => {
  const { t } = useTranslation();
  const Icon =
    state === "completed"
      ? StepDoneIcon
      : state === "active"
        ? StepActiveIcon
        : StepLockedIcon;
  return (
    // A glass bead — the tabs' and count pill's language: a gradient fill under a gradient rim,
    // derived from the state's colour (index.css `.step-badge`). It shows the step's STATE as a
    // glyph (done / on it now / ahead); the order reads from the path itself, and screen readers
    // get both ("Step 2 of 6, Completed"). Opaque, so the card border never runs through it.
    // Straddles the card's TOP border, horizontally centred (inset-x-0 + mx-auto). `z-[1]`: just
    // enough to sit over its own card — at z-10 it tied with the desktop's sticky tab bar and,
    // later in the page, scrolled over it.
    <span
      className={cn(
        "step-badge absolute inset-x-0 z-[1] mx-auto flex items-center justify-center rounded-full",
        // The active step's bead is a size up (36px against 28px): the one the player is on.
        state === "active" ? "-top-[18px] size-9" : "-top-3.5 size-7",
        state === "active"
          ? "step-badge-active"
          : state === "completed"
            ? "step-badge-won"
            : "step-badge-locked"
      )}
    >
      <Icon
        className={
          state === "active"
            ? "size-[18px]"
            : state === "locked"
              ? "size-4"
              : "size-3.5"
        }
      />
      <span className="sr-only">
        {stepOfLabel(position, total, t)}, {stepStateLabel(state, t)}
      </span>
    </span>
  );
};

/** Gold reward line; renders nothing for missions configured without prizes.
 *  Task-prize-only missions show a count here ("3 Prizes") — see
 *  `missionRewardLabel`. */
const RewardTitle = ({
  label,
  size = "sm",
}: {
  label: string | null;
  size?: "sm" | "lg";
}) => {
  if (!label) return null;
  return (
    <PrizeText
      // Never truncated: the reward is the card's priority content (CLAUDE.md) —
      // it shrinks, then glides on its one row, so the rung keeps its height.
      // The reward is the card's headline: 15px on a collapsed step, 20px on the active one
      // (18 / 24 on desktop).
      size={size === "lg" ? "medium" : "body"}
      weight="bold"
      icon
      fit="marquee"
      className="min-w-0"
    >
      {label}
    </PrizeText>
  );
};

/** "+N Prizes" — the reward prizes the line doesn't show (the reward renders
 *  only the most valued one). Borderless like the single-game chip: bordered
 *  pills on these cards read as buttons, and this is a label. */
const MorePrizesBadge = ({ count }: { count: number }) => {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <span className="shrink-0 rounded-full bg-background-card/40 px-2.5 py-1 font-heading text-[11px] leading-3.5 font-medium whitespace-nowrap text-foreground-secondary">
      +{count}{" "}
      {count === 1
        ? t("igwButton.prize", "Prize")
        : t("igwButton.prizes", "Prizes")}
    </span>
  );
};

/**
 * The headline of an OPEN step (the active rung, the landscape detail): the reward centred with
 * the "+N Prizes" count beside it, its games centred UNDER it (the prize leads, the games are
 * secondary — the same order as a prize row), and on a repeatable mission "per round" and the
 * round count beneath. No "In progress" label: the bead and the card's rim already say the state.
 */
const RewardHeader = ({ mission }: { mission: Mission }) => {
  const { t } = useTranslation();
  const repeatable = isRepeatable(mission);
  // Same display-string parser as the bars — progress values are
  // backend-rendered strings, not guaranteed ints, so both ends of "Round X of
  // Y" go through it (a raw `total` would render "5.00" next to a clean X).
  const roundsTotal = Math.max(
    1,
    Math.floor(parseProgressValue(mission.progress?.total) ?? 1)
  );
  // `current` counts COMPLETED rounds; the player is inside the next one.
  const roundsCurrent = Math.min(
    Math.floor(parseProgressValue(mission.progress?.current) ?? 0) + 1,
    roundsTotal
  );
  return (
    <div className="flex w-full flex-col items-center gap-1.5 text-center">
      <span className="flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <RewardTitle label={missionRewardLabel(mission, t)} size="lg" />
        <MorePrizesBadge count={missionRewardExtraCount(mission)} />
      </span>
      <PrizeGamesChips games={missionRewardGames(mission)} centered />
      {repeatable && (
        <span className="flex flex-wrap items-center justify-center gap-2">
          <span className="font-body text-[12px] text-muted-foreground">
            {t("missions.perRound", "per round")}
          </span>
          {mission.progress && (
            <Badge
              variant="ghost"
              color="neutral"
              icon={<Repeat strokeWidth={2} />}
              className="font-medium"
            >
              {t("missions.roundOf", "Round {{current}} of {{total}}", {
                current: roundsCurrent,
                total: roundsTotal,
              })}
            </Badge>
          )}
        </span>
      )}
    </div>
  );
};

/**
 * A step's tasks, read through the step's state. The active rung's rows carry their live variant
 * (`taskVariant`), progress and the turnover caveat — it is a mission the player can work on now.
 * A completed rung is a done receipt; a locked rung a quiet preview (no per-task "Up next" chorus)
 * WITHOUT progress: the mapper sends `{"current": "0.00", total}` for anything unstarted, so a
 * bar there would read as "being worked on". Single-task missions render the same box as
 * multi-task ones — one task anatomy everywhere.
 */
const TaskList = ({
  mission,
  state,
  density,
  className,
}: {
  mission: Mission;
  state: MissionCardState;
  density?: "default" | "dock";
  className?: string;
}) => {
  const { activeCount, hasLiveState } = taskVariantInputs(mission.tasks);
  return (
    <div className={cn("flex w-full flex-col", className)}>
      {mission.tasks.map((task) =>
        state === "active" ? (
          <MissionTaskRow
            key={task.id}
            task={task}
            variant={taskVariant(task, activeCount, hasLiveState)}
            showContributionNote
            density={density}
          />
        ) : (
          <MissionTaskRow
            key={task.id}
            task={task}
            variant={state === "completed" ? "done" : "neutral"}
            showProgress={false}
            density={density}
          />
        )
      )}
    </div>
  );
};

/**
 * Collapsed rung — completed missions (and, dimmed, locked ones). The lock
 * icon + dimming + visible numbering say "not yet" without any unlock copy.
 * The header toggles the mission's task list open (tournament prize rows'
 * accordion pattern): done players can review what they did, locked rungs
 * preview what's ahead.
 */
const CollapsedCard = ({
  mission,
  state,
  stepTotal,
  density,
}: {
  mission: Mission;
  state: "completed" | "locked";
  stepTotal: number;
  density?: "default" | "dock";
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const completed = state === "completed";
  const expandable = mission.tasks.length > 0;

  const taskList = (
    <TaskList
      mission={mission}
      state={state}
      density={density}
      className="px-4 pb-3"
    />
  );

  return (
    // The shared card; `overflow-visible` so the step badge can straddle the top edge. A
    // completed step wears the `won` surface: its reward is the player's now. A locked step dims
    // its CONTENT (below), never the card: a faded surface melted into the desktop well, and the
    // grey lock bead plus muted text say "not yet" on their own.
    <Card inset="none" won={completed} className="relative overflow-visible">
      <EdgePositionBadge
        position={mission.position}
        total={stepTotal}
        state={state}
      />
      {/* Row div (not a button): the games line inside opens a dialog, and a
          button can't nest in a button. The chevron is the real, focusable
          toggle; the row click is a pointer convenience on top. */}
      <div
        onClick={() => expandable && setOpen((o) => !o)}
        className={cn(
          // The reward centred under the bead (the state lives in the bead now, so the row's
          // right end only holds the expand chevron, pinned there; the equal side padding keeps
          // the reward truly centred and clear of it), its games centred under the reward. pt-5
          // leaves 8px under the 28px bead's inside half; the bottom mirrors it.
          "relative flex w-full flex-col items-center gap-1.5 px-10 pt-5 pb-4 text-center",
          !completed && "opacity-55",
          expandable && "cursor-pointer"
        )}
      >
        <span className="flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <RewardTitle label={missionRewardLabel(mission, t)} />
          <MorePrizesBadge count={missionRewardExtraCount(mission)} />
        </span>
        {/* A free-spins reward keeps its games reachable even after completion — the player
            may still want to know where the spins landed. */}
        <PrizeGamesChips games={missionRewardGames(mission)} centered />
        {expandable && (
          <button
            type="button"
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            // Level with the reward's first line, not the row's middle (chips can wrap below it):
            // the line's centre is pt-5 + half its height (20+10 phones, 20+12 desktop), less
            // half the 24px button.
            className="absolute top-[18px] right-3 flex size-6 cursor-pointer items-center justify-center lg:top-5"
          >
            <ChevronDown
              className={cn(
                "size-4 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        )}
      </div>
      {reduceMotion ? (
        open && taskList
      ) : (
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              // Exactly the campaign-switcher dropdown's values — one
              // accordion feel across the widget.
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{
                height: 0,
                opacity: 0,
                transition: { duration: 0.1 },
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 15,
                mass: 0.5,
              }}
              className="overflow-hidden"
            >
              {taskList}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </Card>
  );
};

/** Expanded rung — the player's current mission with its task list. */
const ActiveCard = ({
  mission,
  stepTotal,
  density,
}: {
  mission: Mission;
  stepTotal: number;
  density?: "default" | "dock";
}) => (
  <Card
    active
    // pt-7: the bigger 36px bead's inside half (18px) plus 10px of clearance.
    className="relative flex flex-col gap-3 overflow-visible pt-7"
  >
    <EdgePositionBadge
      position={mission.position}
      total={stepTotal}
      state="active"
    />
    <RewardHeader mission={mission} />
    <TaskList mission={mission} state="active" density={density} />
  </Card>
);

/**
 * A step in the landscape NAVIGATION column (a phone held sideways): the bead on the top edge,
 * the reward, and a chevron pointing into the detail pane beside it, where the step's tasks are.
 * One of a vertical tab list: the steps NOT picked fade lightly (a plain CSS transition), the
 * picked one stands at full strength with its chevron in the text colour — over whatever its
 * state gave it, so a completed step keeps its won gold and the active one its glow. A locked
 * step is dimmer still until it is picked. Progress state stays on the bead and the rim,
 * selection on the fade, so the two never fight over the card.
 */
export const MissionNavCard = ({
  mission,
  state,
  stepTotal,
  selected,
  onSelect,
  id,
  controls,
}: {
  mission: Mission;
  state: MissionCardState;
  stepTotal: number;
  selected: boolean;
  onSelect: () => void;
  /** The tab's element id (`aria-labelledby` of the detail pane). */
  id: string;
  /** The detail pane's element id. */
  controls: string;
}) => {
  const { t } = useTranslation();
  const reward = missionRewardLabel(mission, t);
  const stepOf = stepOfLabel(mission.position, stepTotal, t);
  return (
    <Card
      inset="none"
      won={state === "completed"}
      className={cn(
        "relative overflow-visible transition-opacity duration-300",
        !selected && (state === "locked" ? "opacity-35" : "opacity-50")
      )}
    >
      {/* The bead's own "Step 2 of 6, Completed" is repeated inside the tab, where a screen
          reader lands; hidden here so it is not read twice. */}
      <span aria-hidden="true">
        <EdgePositionBadge
          position={mission.position}
          total={stepTotal}
          state={state}
        />
      </span>
      <button
        type="button"
        role="tab"
        id={id}
        aria-selected={selected}
        aria-controls={controls}
        tabIndex={selected ? 0 : -1}
        onClick={onSelect}
        className={cn(
          // Room on both sides for the chevron, so the reward stays truly centred under the bead.
          // Top padding clears the bead's inside half (18px active, 14px otherwise) by 6px.
          // `focus-ring`: the plain-CSS keyboard ring (index.css); Tailwind's ring / outline
          // utilities don't render in the shadow root.
          "focus-ring focus-ring-inset relative flex w-full cursor-pointer items-center justify-center rounded-(--border-radius) px-9 pb-4 text-center outline-none",
          state === "active" ? "pt-6" : "pt-5"
        )}
      >
        {reward ? (
          <RewardTitle label={reward} />
        ) : (
          // A step without a reward is named by its place, in the reward's type size.
          <span className="font-heading text-[15px] leading-5 font-bold text-foreground">
            {stepOf}
          </span>
        )}
        <span className="sr-only">
          , {stepOf}, {stepStateLabel(state, t)}
        </span>
        <ChevronRight
          aria-hidden="true"
          strokeWidth={selected ? 2.5 : 2}
          className={cn(
            "absolute right-3 size-4 shrink-0 transition-colors duration-300",
            selected ? "text-foreground" : "text-muted-foreground"
          )}
        />
      </button>
    </Card>
  );
};

/**
 * The selected step's DETAIL (the landscape wide pane): the reward headline, then the tasks — the
 * same anatomy as the active rung of the portrait ladder, for whichever step the navigation column
 * picked. The rim follows the step's state (progress colour on the active one, won gold on a
 * completed one) and the tab names the step for screen readers; no bead, since the pane's top edge
 * is a scroller, not a rail.
 */
export const MissionDetailCard = ({
  mission,
  state,
  id,
  labelledBy,
}: {
  mission: Mission;
  state: MissionCardState;
  /** The pane's element id (`aria-controls` of its tab). */
  id: string;
  /** The selected tab's element id. */
  labelledBy: string;
}) => (
  <Card
    id={id}
    role="tabpanel"
    aria-labelledby={labelledBy}
    active={state === "active"}
    won={state === "completed"}
    className="flex flex-col gap-3"
  >
    <RewardHeader mission={mission} />
    <TaskList mission={mission} state={state} />
  </Card>
);

type MissionCardProps = {
  mission: Mission;
  state: MissionCardState;
  /** How many steps the ladder has — for the bead's screen-reader "Step 2 of 6". */
  stepTotal: number;
  /** `dock`: the overlay's narrow column (see `MissionTaskRow`). The active step only — the
   *  dock never shows a collapsed one. */
  density?: "default" | "dock";
};

/** Everything a rung renders from, as one string. The progress stream rebuilds every mission
 *  object on each push (about once a second while playing), though usually one task moved; this
 *  lets the rungs that did not change skip their render. */
const missionFingerprint = (m: Mission): string =>
  JSON.stringify([
    m.id,
    m.position,
    m.isActive,
    m.isCompleted,
    m.progress,
    m.prizes.map((p) => p.title),
    m.tasks.map((t) => [
      t.id,
      t.title,
      t.kind,
      t.isActive,
      t.isCompleted,
      t.progress,
      t.prizes.map((p) => p.title),
    ]),
  ]);

export const MissionCard = memo(
  ({ mission, state, stepTotal, density }: MissionCardProps) =>
    state === "active" ? (
      <ActiveCard mission={mission} stepTotal={stepTotal} density={density} />
    ) : (
      <CollapsedCard
        mission={mission}
        state={state}
        stepTotal={stepTotal}
        density={density}
      />
    ),
  (a, b) =>
    a.state === b.state &&
    a.stepTotal === b.stepTotal &&
    a.density === b.density &&
    (a.mission === b.mission ||
      missionFingerprint(a.mission) === missionFingerprint(b.mission))
);
MissionCard.displayName = "MissionCard";
